import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { RawItem } from "@/types/models";

export interface RawItemTypeaheadProps {
  id?: string;
  rawItems: RawItem[];
  value: string;
  onChange: (rawItemId: string) => void;
  placeholder?: string;
}

export function formatRawItemLabel(item: RawItem): string {
  return item.unitLabel ? `${item.name} (${item.unitLabel})` : item.name;
}

function normalizeSearch(text: string): string {
  return text.trim().toLowerCase();
}

export function RawItemTypeahead({
  id,
  rawItems,
  value,
  onChange,
  placeholder = "Buscar insumo…",
}: RawItemTypeaheadProps) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedItem = useMemo(
    () => rawItems.find((item) => item.id === value) ?? null,
    [rawItems, value]
  );
  const selectedLabel = selectedItem ? formatRawItemLabel(selectedItem) : "";

  const isEditingRef = useRef(false);
  const selectedLabelRef = useRef(selectedLabel);
  selectedLabelRef.current = selectedLabel;

  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState(selectedLabel);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (isEditingRef.current) return;
    setInputText(selectedLabel);
  }, [value, selectedLabel]);

  const filteredItems = useMemo(() => {
    const query = normalizeSearch(inputText);
    if (!query || inputText === selectedLabel) {
      return rawItems;
    }

    return rawItems.filter((item) =>
      formatRawItemLabel(item).toLowerCase().includes(query)
    );
  }, [rawItems, inputText, selectedLabel]);

  useEffect(() => {
    setActiveIndex(0);
  }, [inputText, open]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setOpen(false);
      isEditingRef.current = false;
      setInputText(selectedLabelRef.current);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const selectItem = (item: RawItem) => {
    const label = formatRawItemLabel(item);
    isEditingRef.current = false;
    onChange(item.id);
    setInputText(label);
    setOpen(false);
  };

  const handleFocus = () => {
    isEditingRef.current = true;
  };

  const handleClick = () => {
    setOpen(true);
    isEditingRef.current = true;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let nextValue = event.target.value;
    isEditingRef.current = true;

    if (
      value &&
      selectedLabel &&
      nextValue.startsWith(selectedLabel) &&
      nextValue.length > selectedLabel.length
    ) {
      nextValue = nextValue.slice(selectedLabel.length);
    }

    setInputText(nextValue);
    setOpen(true);

    if (value && nextValue !== selectedLabel) {
      onChange("");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.min(current + 1, Math.max(filteredItems.length - 1, 0)));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const item = filteredItems[activeIndex];
      if (item) selectItem(item);
      return;
    }

    if (event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
      isEditingRef.current = false;
      setInputText(selectedLabel);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid="raw-item-typeahead">
      <Input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && filteredItems[activeIndex]
            ? `${listboxId}-option-${filteredItems[activeIndex].id}`
            : undefined
        }
        value={inputText}
        onChange={handleChange}
        onFocus={handleFocus}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />

      {open && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto border border-primary-200 bg-white shadow-lg dark:border-primary-800 dark:bg-primary-950"
        >
          {filteredItems.length === 0 ? (
            <li className="px-4 py-3 text-sm font-light text-primary-500">
              Nenhum insumo encontrado
            </li>
          ) : (
            filteredItems.map((item, index) => (
              <li
                key={item.id}
                id={`${listboxId}-option-${item.id}`}
                role="option"
                aria-selected={value === item.id || index === activeIndex}
                className={cn(
                  "cursor-pointer px-4 py-3 text-sm font-light text-primary-900 dark:text-white",
                  index === activeIndex && "bg-primary-100 dark:bg-primary-900"
                )}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectItem(item);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {formatRawItemLabel(item)}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
